<template>
    <div class="event-wrapper" @click="onEventClick({ eventId: event.id, hardwareId: event.hardwareId })">
        <div class="warning-background" :class="bgClassObject">
            <icon v-if="event.eventClassification === 2" icon="check" :height="30" :width="30"></icon>
            <span v-else-if="event.eventClassification === 3">?</span>
            <span v-else>!</span>
        </div>
        <div class="cursor" :class="cursorClassObject">
            <div :class="['event-type', eventName(event) || '']" >{{ eventName(event) }}</div>
            <div class="details">
                <div class="date event-name">{{ date(event.triggerOn ) }}</div>
                <div class="details--speed">
                    <div class="speed-limit">{{ speedOrDistanceUnitManager(event.speedLimit) }}</div>
                    <div class="speed-value">{{ formatSpeedUnit(event.speed) }}</div>
                </div>
            </div>
            <div class="details" v-if="driverInfo">
                <div class="car">
                    <icon class="icon-margin first-icon" icon="event-car" :width="14"></icon>{{ event.vehicleRegistrationNumber || 'Unknown' }}
                    <icon class="icon-margin" icon="people" :width="15"></icon>{{ event.driverName || 'Unknown' }} &nbsp;
                    <icon class="icon-margin" icon="suitcase" :width="13"></icon>{{ event.fleetName || 'Unknown' }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import { mapGetters, mapState } from 'vuex'
import { formatDate, searchEnumById, calculateSpeedOrDistanceUnit, speedOrDistanceUnitManager } from '@/helpers/Utils'
import Icon from '@/components/atoms/Icon'

export default {
    name: 'EventItem',
    components: { Icon },
    data () {
        return {
            bgClassObject: {
                'warning-background--green': this.event.eventClassification === 2,
                'warning-background--amber': this.event.eventClassification === 3,
                'warning-background--red': this.event.eventClassification === 4
            },
            cursorClassObject: {
                'cursor--green': this.event.eventClassification === 2,
                'cursor--amber': this.event.eventClassification === 3,
                'cursor--red': this.event.eventClassification === 4
            }
        }
    },
    props: {
        event: {
            type: Object,
            required: true
        },
        driverInfo: {
            type: Boolean,
            default: true
        },
        onEventClick: {
            type: Function,
            required: false
        }
    },
    computed: {
        ...mapGetters({
            getEventTypes: 'dictionaryModule/getEventTypes',
            getDeviceCommandStates: 'dictionaryModule/getDeviceCommandStates',
            getSpeedUnit: 'authModule/getSpeedUnit'
        }),
    },
    methods: {
        date (date) {
            return formatDate(date)
        },
        speedOrDistanceUnitManager (speed) {
            return speedOrDistanceUnitManager(speed)
        },
        formatSpeedUnit (speed) {
            return 0 === this.getSpeedUnit ? speed + ' kph' : calculateSpeedOrDistanceUnit(speed, 'mi') + ' mph'
        },
        eventName (eventObject) {
            const event = this.getEventTypes.find((elem) => {
                return elem.id === eventObject.eventTypes[0]
            })

            return event.key || 'Unknown'
        },
        getMediaStatus (status) {
            return searchEnumById(this.getDeviceCommandStates, status)
        }
    }
}

</script>

<style scoped lang="scss">

.event-wrapper {
    box-shadow: 5px 5px 5px #b0b0b0;
    transition: border 0.3s;
    cursor: pointer;
    border-top: 1px solid transparent;
    border-right: 1px solid transparent;
    border-bottom: 2px solid transparent;

    &:hover {
        border-top: 1px solid $secondary-default;
        border-right: 1px solid $secondary-default;
        border-bottom: 2px solid $secondary-default;
    }
}

.icon-margin {
    margin: 0 5px 0 10px;
}

.first-icon {
    margin: 0 5px 0 0;
}

.cursor {
    padding: 20px 20px 5px;
    cursor: pointer;

    &--green {
        border-left: 4px solid #189803;
    }

    &--amber {
        border-left: 4px solid #f5ad1e;
    }

    &--red {
        border-left: 4px solid $primary-default;
    }
}

.cursor {
    &--green .event-type {
        color: #189803 !important;
    }

    &--amber .event-type {
        color: #f5ad1e !important;
    }

    &--red .event-type {
        color: $primary-default !important;
    }
}

.event-type {
    color: $primary-default;
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 6px;
    text-transform: uppercase;
}

.warning-background {
    position: absolute;
    right: -20px;
    bottom: -13px;
    color: $secondary-default;
    border: 7px solid $secondary-default;
    padding: 26px 25px 20px;
    border-radius: 50px;
    font-size: 50px;
    font-family: 'Hind', sans-serif;
    font-weight: 700;
    opacity: 0.1;

    &--green {
        color: #189803;
        border: 7px solid #189803;
        padding: 16px 13px 10px;
        border-radius: 50px;
        font-size: 30px;
    }

    &--amber {
        color: #f5ad1e;
        border: 7px solid #f5ad1e;
        padding: 25px 22px 20px;
        border-radius: 50px;
        font-size: 30px;
    }

    &--red {
        color: $primary-default;
        border: 7px solid $primary-default;
        padding: 26px 25px 20px;
        border-radius: 50px;
        font-size: 30px;
    }
}

.details {
    display: flex;
    font-size: 12px;
    font-weight: 400;
    padding-bottom: 7px;

    &--speed {
        display: flex;
        flex-direction: row;
    }

    .date {
        color: $secondary-default;
        margin-right: 70px;
    }

    .status {
        font-weight: bold;
        text-transform: uppercase;
    }

    .speed-text,
    .speed-value,
    .speed-limit {
        font-weight: bold;
    }

    .speed-limit {
        &::after {
            content: "|";
            font-weight: bold;
            margin: 0 3px;
        }
    }
}

.event-wrapper {
    position: relative;
    overflow: hidden;
    box-shadow: 0 2px 9px #b0b0b0;
}

.journey-events__list {
    .cursor {
        cursor: pointer;
    }

    .warning-background {
        &--amber {
            padding: 5px 20px 5px;
        }

        &--red {
            padding: 8px 24px 5px;
        }

        &--green {
            padding: 6px 13px 5px;
        }
    }

    .details .date {
        margin-right: 55px;
    }

    .event-wrapper {
        margin: 10px 0;
    }
}

</style>
